/*
 * Copyright (c) 2024 Roboto Technologies, Inc.
 */

import { List } from "@mui/material";
import * as React from "react";

import { AuthZCategoryListItem } from "@/shared/components/AuthZCategoryListItem";
import { useAPICall } from "@/shared/services/apiHooks";
import { AuthZTuple, GetAccessResponse, RobotoEndpoint } from "@/types";

import { useDatasetContext } from "../useDatasetContext";

const datasetsAccessEndpoint: RobotoEndpoint = (pathParams) => {
  const datasetId = pathParams?.datasetId;

  return `/datasets/${datasetId}/access`;
};

/**
 * Renders the "Permissions" tab of the dataset detail view as a list of
 * authorization categories for the dataset identified by the `datasetId`
 * on the parent dataset outlet context.
 *
 * On mount and whenever the dataset id changes:
 *   1. GET `/datasets/{datasetId}/access` via `useAPICall`.
 *   2. Bucket the returned tuples by `relation`.
 *   3. Render one `AuthZCategoryListItem` per fixed category, in order:
 *      Owner, Groups (`associated_group`), Collections (`parent`),
 *      Viewer, Editor. A category with no tuples renders with an empty list.
 *
 * Owner, Groups, and Collections are read-only (`allowEdit={false}`); Viewer
 * and Editor are user-editable (`allowEdit={true}`). All five list items are
 * wired to the same `/datasets/{datasetId}/access` endpoint for writes and
 * call `refetch` via `onChange` after a successful edit, so the displayed
 * tuples reflect the post-write state.
 */
export const DatasetPermissions: React.FC = () => {
  const { datasetId } = useDatasetContext();
  const { data, loading, refetch } = useAPICall<GetAccessResponse>({
    endpoint: datasetsAccessEndpoint,
    method: "GET",
    pathParams: { datasetId: datasetId },
  });

  React.useEffect(() => {
    refetch({
      pathParams: { datasetId: datasetId },
    });
  }, [datasetId, refetch]);

  const relationsToTuples: Map<string, AuthZTuple[]> = new Map();

  if (data) {
    data.data.relations.forEach((relation) => {
      const tuples = relationsToTuples.get(relation.relation) || [];
      tuples.push(relation);
      relationsToTuples.set(relation.relation, tuples);
    });
  }

  const editAccessEndpointPathParams = { datasetId: datasetId };

  return (
    <div>
      <List sx={{ width: "100%", padding: 0 }}>
        <AuthZCategoryListItem
          allowEdit={false}
          displayName={"Owner"}
          editAccessEndpoint={datasetsAccessEndpoint}
          editAccessEndpointPathParams={editAccessEndpointPathParams}
          onChange={refetch}
          relationName={"owner"}
          relations={relationsToTuples.get("owner") || []}
          resourceId={datasetId}
          resourceType={"dataset"}
          loading={loading}
        />
        <AuthZCategoryListItem
          allowEdit={false}
          displayName={"Groups"}
          editAccessEndpoint={datasetsAccessEndpoint}
          editAccessEndpointPathParams={editAccessEndpointPathParams}
          onChange={refetch}
          relationName={"associated_group"}
          relations={relationsToTuples.get("associated_group") || []}
          resourceId={datasetId}
          resourceType={"dataset"}
          loading={loading}
        />
        <AuthZCategoryListItem
          allowEdit={false}
          displayName={"Collections"}
          editAccessEndpoint={datasetsAccessEndpoint}
          editAccessEndpointPathParams={editAccessEndpointPathParams}
          onChange={refetch}
          relationName={"parent"}
          relations={relationsToTuples.get("parent") || []}
          resourceId={datasetId}
          resourceType={"dataset"}
          loading={loading}
        />
        <AuthZCategoryListItem
          allowEdit={true}
          displayName={"Viewer"}
          editAccessEndpoint={datasetsAccessEndpoint}
          editAccessEndpointPathParams={editAccessEndpointPathParams}
          onChange={refetch}
          relationName={"viewer"}
          relations={relationsToTuples.get("viewer") || []}
          resourceId={datasetId}
          resourceType={"dataset"}
          loading={loading}
        />
        <AuthZCategoryListItem
          allowEdit={true}
          displayName={"Editor"}
          editAccessEndpoint={datasetsAccessEndpoint}
          editAccessEndpointPathParams={editAccessEndpointPathParams}
          onChange={refetch}
          relationName={"editor"}
          relations={relationsToTuples.get("editor") || []}
          resourceId={datasetId}
          resourceType={"dataset"}
          loading={loading}
        />
      </List>
    </div>
  );
};
